import { Box } from '@chakra-ui/react';
import {
  useDaily,
  useDailyEvent,
  useMeetingState,
} from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { BerryLoader } from '@web-app/components/BerryLoader';
import { DisclosureText } from '@web-app/components/CallV2/DisclosureText/DisclosureText';
import { useDailyRoom } from '@web-app/components/CallV2/hooks/useDailyRoom';
import { LeaveCallModalV2 } from '@web-app/components/CallV2/LeaveCallModal/LeaveCallModalV2';
import { CallQualitySurveyModal } from '@web-app/components/Daily/CallQualitySurveyModal/CallQualitySurveyModal';
import { ChatNotificationSound } from '@web-app/components/Daily/ChatNotificationSound/ChatNotificationSound';
import { DailyCallProvider } from '@web-app/components/Daily/DailyCallProvider/DailyCallProvider';
import { DailyPrebuilt } from '@web-app/components/Daily/DailyPrebuilt/DailyPrebuilt';
import { ReadyToLeaveNotification } from '@web-app/components/Daily/ReadyToLeaveNotification';
import { RoomErrorScreen } from '@web-app/components/Daily/RoomErrorScreen';
import { ROOM_ERROR_TYPES } from '@web-app/components/Daily/RoomErrorScreen/types';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useVerifyRoom } from '@web-app/hooks/daily/verify-room';
import { useAppointmentByRoomId } from '@web-app/hooks/queries/appointment-by-room-name';
import { useParticipants } from '@web-app/hooks/useParticipants';
import { usePatientVideoCallAnalytics } from '@web-app/hooks/usePatientVideoCallAnalytics';
import { useRedirectToPatientApp } from '@web-app/hooks/useRedirectToPatientApp';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { PatientPostCallV2 } from '../PatientPostCallV2/PatientPostCallV2';

/**
 * Inner component that handles survey logic for patients.
 * Must be inside DailyProvider to access Daily hooks.
 */
const PatientCallWithSurvey: FC<{ roomId: string }> = ({ roomId }) => {
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showPostCall, setShowPostCall] = useState(false);
  const [hasJoinedMeeting, setHasJoinedMeeting] = useState(false);
  const [hasSeenOtherParticipant, setHasSeenOtherParticipant] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const redirectToPatientApp = useRedirectToPatientApp();
  const { data: appointment } = useAppointmentByRoomId(roomId);
  const meetingState = useMeetingState();
  const { participantsCount } = useParticipants();
  const daily = useDaily();
  const { trackEvent } = useAnalytics();

  // Track video call analytics (patient joined/left)
  usePatientVideoCallAnalytics({
    roomId,
    appointmentId: appointment?.healthie_id,
    clientId: appointment?.client_id,
    providerId: appointment?.provider_id,
  });

  // Track when patient joins the meeting
  const hasLoggedJoin = useRef(false);
  useEffect(() => {
    if (meetingState === 'joined-meeting') {
      setHasJoinedMeeting(true);

      if (!hasLoggedJoin.current) {
        hasLoggedJoin.current = true;
        datadogLogs.logger.info(
          'Patient is ready and requested to join',
          callLogContext('call-join', {
            roomId,
            participantType: 'patient',
            clientId: appointment?.client_id,
            providerId: appointment?.provider_id,
            appointmentId: appointment?.healthie_id,
          }),
        );
      }
    }
  }, [meetingState, roomId, appointment]);

  // Track when patient has seen another participant (means they were admitted from waiting room)
  useEffect(() => {
    if (participantsCount > 1) {
      setHasSeenOtherParticipant(true);
    }
  }, [participantsCount]);

  // Listen for left-meeting event to show survey
  // Only show if patient was actually in a meeting (not on initial mount)
  useDailyEvent(
    'left-meeting',
    useCallback(() => {
      if (hasJoinedMeeting) {
        setShowPostCall(true);
        if (!surveyCompleted) {
          setShowSurvey(true);
        }
      }
    }, [surveyCompleted, hasJoinedMeeting, setShowPostCall, setShowSurvey]),
  );

  // Listen for custom "Leave Session" button click from Daily's tray
  const handleCustomButtonClick = useCallback(
    (event: { button_id?: string } | null) => {
      if (event && 'button_id' in event && event.button_id === 'leaveSession') {
        setIsLeaveModalOpen(true);
      }
    },
    [],
  );
  useDailyEvent('custom-button-click', handleCustomButtonClick);

  /**
   * True if the "ready to leave" popup should be displayed.
   * Only show if:
   * - Patient is currently alone (participantsCount === 1)
   * - Patient has previously seen another participant (was admitted from waiting room)
   */
  const showReadyToLeave =
    meetingState === 'joined-meeting' &&
    participantsCount === 1 &&
    hasSeenOtherParticipant;

  // Log when provider leaves and patient sees the ready-to-leave countdown
  const hasLoggedProviderLeft = useRef(false);
  useEffect(() => {
    if (showReadyToLeave && !hasLoggedProviderLeft.current) {
      hasLoggedProviderLeft.current = true;
      datadogLogs.logger.info(
        'Provider left, ready-to-leave countdown started',
        callLogContext('call-leave', {
          roomId,
          participantType: 'patient',
        }),
      );
    }
  }, [showReadyToLeave, roomId]);

  const handleSurveyClose = useCallback(() => {
    setSurveyCompleted(true);
    setShowSurvey(false);
  }, []);

  const handleLeave = useCallback(() => {
    if (daily) {
      daily.leave();
    }
  }, [daily]);

  const handleCancelLeave = useCallback(() => {
    setIsLeaveModalOpen(false);
    trackEvent(EVENT_NAMES.CLICKS_RETURN_TO_CALL);
  }, [trackEvent]);

  const handleConfirmLeave = useCallback(() => {
    setIsLeaveModalOpen(false);
    handleLeave();
  }, [handleLeave]);

  const shouldShowSurvey = showSurvey && !surveyCompleted;

  return (
    <>
      {shouldShowSurvey && (
        <CallQualitySurveyModal
          onSubmit={handleSurveyClose}
          onSkip={handleSurveyClose}
          dailyRoomId={roomId}
          healthieAppointmentId={appointment?.healthie_id}
          duration={0}
          callExperienceVersion={2}
        />
      )}

      {showReadyToLeave && <ReadyToLeaveNotification onLeave={handleLeave} />}
      <LeaveCallModalV2
        isOpen={isLeaveModalOpen}
        onClose={handleCancelLeave}
        onConfirm={handleConfirmLeave}
      />

      {/* Chat notification sound - plays chime when messages are received */}
      <ChatNotificationSound />

      {showPostCall ? (
        <PatientPostCallV2 redirectToPatientApp={redirectToPatientApp} />
      ) : (
        <Box
          width="100vw"
          height="100vh"
          position="relative"
          display="flex"
          flexDirection="column"
          data-testid="patient-call-layout"
        >
          <Box flex="1" overflow="hidden" minHeight="0">
            <DailyPrebuilt />
          </Box>

          {/* Disclosure text - only show in pre-join states */}
          {meetingState &&
            ['new', 'loading', 'loaded', 'joining-meeting'].includes(
              meetingState,
            ) && (
              <Box
                bg="surface.default"
                width="100%"
                py={4}
                flexShrink={0}
                data-testid="patient-disclosure-text"
              >
                <DisclosureText />
              </Box>
            )}
        </Box>
      )}
    </>
  );
};

/**
 * Patient-facing call layout using Daily Prebuilt UI.
 * Renders the video call interface in full-screen mode.
 * Verifies room exists before rendering DailyCallProvider.
 */
export const PatientCallLayout: FC = () => {
  const { roomId, roomUrl } = useDailyRoom();
  const {
    isPending: isVerifyingRoom,
    isRedirecting,
    error: verifyRoomError,
  } = useVerifyRoom(roomId, 'patient');

  if (isRedirecting || isVerifyingRoom) {
    return <BerryLoader />;
  }

  // Handle definitive errors (ended/unavailable) with error screen
  if (verifyRoomError?.reason === 'ended') {
    datadogLogs.logger.info(
      'Patient room verify: session has ended',
      callLogContext('room-verification', {
        roomId: roomId as string,
        participantType: 'patient',
      }),
    );
    return (
      <RoomErrorScreen
        state={{ type: ROOM_ERROR_TYPES.APPOINTMENT_ENDED_PATIENT }}
      />
    );
  }
  if (verifyRoomError?.reason === 'unavailable') {
    datadogLogs.logger.info(
      'Patient room verify: appointment unavailable',
      callLogContext('room-verification', {
        roomId: roomId as string,
        participantType: 'patient',
      }),
    );
    return (
      <RoomErrorScreen
        state={{ type: ROOM_ERROR_TYPES.APPOINTMENT_CANCELLED }}
      />
    );
  }
  if (verifyRoomError?.reason === 'not_found') {
    datadogLogs.logger.warn(
      'Patient room verify: appointment not found',
      callLogContext('room-verification', {
        roomId: roomId as string,
        participantType: 'patient',
      }),
    );
    return (
      <RoomErrorScreen
        state={{ type: ROOM_ERROR_TYPES.APPOINTMENT_NOT_FOUND }}
      />
    );
  }

  return (
    <DailyCallProvider roomUrl={roomUrl}>
      {/* roomId is guaranteed to exist: parent CallPageRootV2 guards with {!roomId ? <Loading/> : ...} */}
      <PatientCallWithSurvey roomId={roomId as string} />
    </DailyCallProvider>
  );
};
